import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { AccountManagementInvitationModel } from 'src/app/models/api/accountManagement.model';
import { MenuAction } from 'src/app/models/menuAction.model';
import { ISelectOption } from 'src/app/models/selectOptions';
import { ITableColumn } from 'src/app/models/tableColumns';
import { TableColumnTypeEnum, WidthType } from 'src/app/models/tableColumns/tableColumn.interface';
import { IRowViewModel } from 'src/app/models/viewModels/rowViewModel';
import { SupportedHrefTypes } from 'src/app/models/viewModels/supportedHrefTypes';
import { ITableRowViewModel, QueryParametersModel } from 'src/app/models/viewModels/tableRowViewModel';
import { LabelsProviderPipe } from 'src/app/pipes/labels-provider/labels-provider.pipe';
import { AccountManagementHttpService } from 'src/app/services/httpServices/accountManagementHttpService/account-management-http-service.service';

export interface RowClicked {
  row: BehaviorSubject<ITableRowViewModel>;
}

export interface RowActionClicked extends RowClicked {
  action: MenuAction;
}

export interface ToggleClicked extends RowClicked {
  toggleState: boolean;
}
export interface OptionSelected {
  option: ISelectOption;
  id: string;
}

export interface DeleteClicked {
  id: string;
  moreData: ITableRowViewModel;
}
export interface CopyClicked {
  id: string;
  moreData: ITableRowViewModel;
}
export interface CheckboxClicked {
  row: BehaviorSubject<ITableRowViewModel>;
  id: string;
  status: boolean;
}

export interface ExpandFilterResultClicked {
  status: boolean;
}

@Component({
  selector: 'app-table-row',
  templateUrl: './table-row.component.html',
  styleUrls: ['./table-row.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class TableRowComponent implements OnInit {
  @Input() public rowColumns: ITableColumn[] = [];
  @Input() public behaviorSubjectRow: BehaviorSubject<ITableRowViewModel>;
  @Input() public withActions = false;
  @Input() public withActionsIcon = false;
  @Input() public withCheckbox = false;
  @Input() public checkboxEditable = true;
  @Input() public withDeleteIcon = true;
  @Input() public withCopyIcon = false;
  @Input() public dropDownOptions: ISelectOption[] = [];

  @Output() public rowOnClick = new EventEmitter<RowClicked>();
  @Output() public rowActionOnClick = new EventEmitter<RowActionClicked>();
  @Output() public toggleOnClick = new EventEmitter<ToggleClicked>();
  @Output() public checkboxOnClick = new EventEmitter<CheckboxClicked>();
  @Output() public deleteRowOnClick = new EventEmitter<DeleteClicked>();
  @Output() public copyRowOnClick = new EventEmitter<CopyClicked>();
  @Output() public rowOnChangeState = new EventEmitter<RowClicked>();
  @Output() public dropdownOnSelection = new EventEmitter<OptionSelected>();

  public rowViewModel: ITableRowViewModel;

  public rowClassName: string;
  public isMenuOpen = false;
  public shouldOpenDialog = false;
  public menuActions$: BehaviorSubject<MenuAction[]>;
  public menuActions: MenuAction[];

  public colTypesEnum = TableColumnTypeEnum;
  public supportedHrefTypes = SupportedHrefTypes;
  public columnsTotalStaticPixels = 0;
  public WidthTypeEnum = WidthType;

  public notAvailableString: string;

  private readonly _defaultRowClassName = 'row body-row';
  private readonly _defaultNotClickableClassName = 'row body-row-not-clickable';

  constructor(
    private readonly _router: Router,
    private readonly _labelsProviderPipe: LabelsProviderPipe,
    private readonly _accountManagementHttpService: AccountManagementHttpService
  ) {
    this.notAvailableString = this._labelsProviderPipe.transform('common.notAvailable');
  }

  ngOnInit(): void {
    this.rowColumns.forEach((column) => {
      if (column.widthType == WidthType.Pixel) {
        this.columnsTotalStaticPixels += column.width;
      }
    });

    this.behaviorSubjectRow.subscribe((row) => {
      // clone row, pipe-cell detect changes of row object when behaviorSubject emits new values with "next"
      this.rowViewModel = Object.assign({}, row);
      this._updateRowClass();

      if (row.menuActions) {
        this.menuActions = row.menuActions;
        if (!this.menuActions$) {
          this.menuActions$ = new BehaviorSubject(row.menuActions);
        } else {
          this.menuActions$.next(row.menuActions);
        }
      }
      // if (row.isSelected) {
      //     const element = document.getElementsByClassName("body-row-clicked")
      //     if (element.length == 1) {
      //         element[0].scrollIntoView({ block: 'center' });
      //     }
      // }
    });

    // init clickable to default true
    if (this.behaviorSubjectRow.getValue().clickable == undefined) {
      this.rowViewModel.clickable = true;
    }
  }

  public onRowClick(): void {
    //this.rowViewModel.isSelected = true
    this._updateRowClass();

    if (this.behaviorSubjectRow.getValue().clickable) {
      this.rowOnClick.emit({
        row: this.behaviorSubjectRow,
      });
    }
  }

  public onMenuClick(): void {
    this.isMenuOpen = !this.isMenuOpen;
    this._updateRowClass();
  }

  public onCheckboxClick(): void {
    this.behaviorSubjectRow.value.checkboxStatus = !this.behaviorSubjectRow.value.checkboxStatus;
    this.checkboxOnClick.emit({
      row: this.behaviorSubjectRow,
      id: this.behaviorSubjectRow.value.rowId,
      status: this.behaviorSubjectRow.value.checkboxStatus,
    } as CheckboxClicked);
  }

  public onOutsideMenuClick(): void {
    if (this.isMenuOpen) {
      this.isMenuOpen = false;
      this._updateRowClass();
    }
  }

  public actionOnClick(action: MenuAction) {
    this.isMenuOpen = false;
    this._updateRowClass();

    this.rowActionOnClick.emit({
      row: this.behaviorSubjectRow,
      action: action,
    });

    if (action.shouldOpenDialog) {
      this._addRowIdToUrl();
    }
  }

  public onToggleClick(state: boolean) {
    this.toggleOnClick.emit({
      row: this.behaviorSubjectRow,
      toggleState: state,
    });
  }

  public onDeleteIconClicked() {
    this.deleteRowOnClick.emit({
      id: this.behaviorSubjectRow.value.rowId,
      moreData: this.behaviorSubjectRow.value,
    } as DeleteClicked);
  }

  public async onCopyIconClicked() {
    try {
      const rowData = this.behaviorSubjectRow.value as IRowViewModel;
      const invitationId = rowData.rowId;

      if (!invitationId) {
        return;
      }

      const invitations = await firstValueFrom(this._accountManagementHttpService.getInvitations());
      const invitation = invitations.find((inv: AccountManagementInvitationModel) => inv.id === invitationId);

      if (!invitation) {
        return;
      }

      const invitationUrl = (invitation as AccountManagementInvitationModel).invitationUrl;

      if (!invitationUrl) {
        return;
      }

      await navigator.clipboard.writeText(invitationUrl);

      this.copyRowOnClick.emit({
        id: rowData.rowId,
        moreData: rowData,
      } as CopyClicked);
    } catch (error) {
      console.error('Failed to copy invitation URL:', error);
    }
  }

  public shouldShowCopyIcon(): boolean {
    return !this.rowViewModel?.isUser;
  }

  public onRowOnChangeState(changed: boolean) {
    const rowValue = this.behaviorSubjectRow.value;
    rowValue.statusChanged = changed;

    this.rowOnChangeState.emit({
      row: this.behaviorSubjectRow,
    });
  }

  public onDropDownSelectOption(option: ISelectOption) {
    this.dropdownOnSelection.emit({
      id: this.behaviorSubjectRow.value.rowId,
      option: option,
    } as OptionSelected);
  }

  private _updateRowClass() {
    this.rowClassName = this._defaultRowClassName;
    if (!this.rowViewModel.clickable) {
      this.rowClassName = this._defaultNotClickableClassName;
    }
    if (this.rowViewModel.className) {
      this.rowClassName = this.rowClassName + ' ' + this.rowViewModel.className;
    }
    // if (this.rowViewModel.clickable && this.rowViewModel.isSelected) {
    //     this.rowClassName = this.rowClassName + " body-row-clicked"
    // }
    if (this.rowViewModel.isLastRow) {
      this.rowClassName = this.rowClassName + ' last-row';
    }
  }

  private _addRowIdToUrl(queryParametersOnClick?: QueryParametersModel) {
    if (!queryParametersOnClick) {
      queryParametersOnClick = {};
    }
    this._router.navigate([], {
      queryParams: queryParametersOnClick,
      queryParamsHandling: 'merge',
    });
  }
}
